import { ActorsPool } from '../ActorManager';
import { Principal } from '@dfinity/principal';
import { TokenUtils } from '../TokenUtils';
import { map, get } from 'lodash';
import { LoggerEvents } from '../../types/LoggerEvents';
export class OrigynNFTRepository {
    /**
     * @param {IIAMRepository} iamRepository
     * @param {LoggerService} logger
     */
    constructor(iamRepository, logger) {
        this.iamRepository = iamRepository;
        this.logger = logger;
    }
    /**
     * Gets the ownership history of the NFT Token
     * @param {string} canisterId
     * @param {boolean} fetchUsername
     */
    async getNFTOwnershipHistory(canisterId, fetchUsername) {
        const nftActor = await ActorsPool.getOrigynNFTCanisterActor(canisterId);
        const ownershipHistoryResponse = await nftActor.get_ownership_history();
        const promises = map(ownershipHistoryResponse, async (ownership) => {
            const principal = ownership[1].toText();
            let username;
            if (fetchUsername) {
                try {
                    username = await this.iamRepository.getUsernameByPrincipal(principal);
                }
                catch (e) {
                    this.logger.error(e, {
                        tags: {
                            event: LoggerEvents.GetOrigynNFTOwner,
                        },
                        contexts: {
                            token: {
                                principal,
                                canisterId,
                            },
                        },
                    });
                }
            }
            return {
                time: (ownership[0] / BigInt(1000000)).toString(),
                principal,
                username,
            };
        });
        return Promise.all(promises);
    }
    /**
     * Gets a payload of NFT Token
     * @param {string} canisterId
     */
    async getNFTRawData(canisterId) {
        const nftActor = await ActorsPool.getOrigynNFTCanisterActor(canisterId);
        let data;
        const rawDataResponse = await nftActor.get_raw_data();
        const rawData = rawDataResponse[0];
        if (rawData) {
            data = TokenUtils.rawDataToObject(rawData[1]);
        }
        return OrigynNFTRepository.getOrigynNFTTokenPayload(data);
    }
    async getNFTData(tokenId) {
        let payload = OrigynNFTRepository.getOrigynNFTTokenPayload();
        let owner;
        let ownershipHistory = [];
        try {
            payload = await this.getNFTRawData(tokenId);
        }
        catch (e) {
            this.logger.error(e, {
                tags: {
                    event: LoggerEvents.GetOrigynNFTRawData,
                },
                contexts: {
                    token: {
                        id: tokenId,
                    },
                },
            });
        }
        try {
            owner = await this.getNFTOwner(tokenId);
        }
        catch (e) {
            this.logger.error(e, {
                tags: {
                    event: LoggerEvents.GetOrigynNFTOwner,
                },
                contexts: {
                    token: {
                        id: tokenId,
                    },
                },
            });
        }
        try {
            ownershipHistory = await this.getNFTOwnershipHistory(tokenId, true);
        }
        catch (e) {
            this.logger.error(e, {
                tags: {
                    event: LoggerEvents.GetOrigynOwnershipHistory,
                },
                contexts: {
                    token: {
                        id: tokenId,
                    },
                },
            });
        }
        return {
            payload,
            owner,
            ownershipHistory,
        };
    }
    async getNFTOwner(tokenId) {
        const actor = await ActorsPool.getOrigynNFTCanisterActor(tokenId);
        const ownerResponse = await actor.get_owner();
        const owner = ownerResponse[0];
        if (!owner) {
            return;
        }
        return owner[1].toText();
    }
    async transfer(tokenId, principal) {
        const actor = await ActorsPool.getOrigynNFTCanisterActor(tokenId);
        await actor.transfer(Principal.fromText(principal));
    }
    async getNFTHistory(tokenId) {
        const actor = await ActorsPool.getOrigynNFTCanisterActor(tokenId);
        const history = await actor.get_raw_data_history();
        return OrigynNFTRepository.prepareNFTTokenHistory(history);
    }
    /**
     * Converts unknown token payload to NFTTokenPayload
     * @param {unknown | undefined} payload
     * @return {NFTTokenPayload}
     */
    static getOrigynNFTTokenPayload(payload) {
        return {
            external_url: get(payload, 'external_url', ''),
            description: get(payload, 'description', ''),
            name: get(payload, 'name', ''),
            images: map(get(payload, 'images'), (image, index) => ({
                key: index,
                source: {
                    uri: get(image, 'value', ''),
                },
            })),
            image: {
                uri: get(payload, 'image'),
            },
            attributes: map(get(payload, 'attributes'), (attribute) => ({
                type: get(attribute, 'trait_type', ''),
                value: get(attribute, 'value', ''),
            })),
        };
    }
    /**
     * Parses raw NFT version history
     * @param {RawData[]} rawHistory
     * @return {NFTTokenHistory[]}
     */
    static prepareNFTTokenHistory(rawHistory) {
        return map(rawHistory, (historyItem) => {
            const payload = TokenUtils.rawDataToObject(historyItem[1]);
            return {
                updatedAt: (historyItem[0] / BigInt(1000000)).toString(),
                payload: OrigynNFTRepository.getOrigynNFTTokenPayload(payload),
                updatedBy: historyItem[2].toText(),
            };
        });
    }
}
//# sourceMappingURL=OrigynNFTRepository.js.map